// Matches potential credit card numbers:
// - 3-4 groups of 4-6 digits separated by a single space or dash (e.g. "4111 1111 1111 1111")
// - 13-19 continuous digits (e.g. "4111111111111111")
// Uses specific group sizes to avoid greedy overconsumption when card numbers are
// followed by other digit sequences (e.g. "4111 1111 1111 1111 09/25 463").
const CREDIT_CARD_PATTERN =
  /(?<!\d)(\d{4,6}[\s-]\d{4,6}[\s-]\d{4,6}(?:[\s-]\d{1,7})?|\d{13,19})(?!\d)/g;
const URL_PATTERN = /https?:\/\/\S+/gi;
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Keywords that indicate a credit card context. When these appear near a digit sequence,
// we skip the Luhn check to catch card numbers with typos or non-standard check digits.
const CONTEXT_KEYWORDS = /\b(card|credit|debit|cc|mc|exp|cvv|cvc|visa|mastercard|amex|discover)\b/i;
const CONTEXT_WINDOW = 50;

export const sanitizeCreditCard = (value) => {
  if (!value) {
    return value;
  }

  // Protect URLs from false-positive matches (e.g. long numeric IDs in URL paths)
  const urls = [];
  let protected_ = value.replace(URL_PATTERN, (url) => {
    urls.push(url);
    return `\x00URL${urls.length - 1}\x00`;
  });

  // Protect UUIDs/GUIDs from false-positive matches (e.g. digit groups in hex UUIDs)
  const uuids = [];
  protected_ = protected_.replace(UUID_PATTERN, (uuid) => {
    uuids.push(uuid);
    return `\x00UUID${uuids.length - 1}\x00`;
  });

  const sanitized = protected_.replace(CREDIT_CARD_PATTERN, (match, _group, offset) => {
    const digitsOnly = match.replace(/[\s-]/g, "");

    if (!isValidCardLength(digitsOnly)) {
      return match;
    }

    if (
      !isLuhnValid(digitsOnly) &&
      !hasKnownCardPrefix(digitsOnly) &&
      !hasCreditCardContext(protected_, offset, match.length)
    ) {
      return match;
    }

    return maskCardNumber(digitsOnly);
  });

  const restored = sanitized.replace(/\x00UUID(\d+)\x00/g, (_, index) => uuids[index]);
  return restored.replace(/\x00URL(\d+)\x00/g, (_, index) => urls[index]);
};

const hasCreditCardContext = (text, offset, matchLength) => {
  const start = Math.max(offset - CONTEXT_WINDOW, 0);
  const end = Math.min(offset + matchLength + CONTEXT_WINDOW, text.length);
  const surrounding = text.slice(start, end);
  return CONTEXT_KEYWORDS.test(surrounding);
};

// Matches known card issuer prefixes (IIN/BIN ranges) to catch card numbers
// that fail Luhn (e.g. typos) even without surrounding context keywords.
const hasKnownCardPrefix = (digits) => {
  const len = digits.length;
  if (/^4/.test(digits) && len >= 13) return true; // Visa
  if (/^5[1-5]/.test(digits) && len === 16) return true; // Mastercard
  if (/^2[2-7]/.test(digits) && len === 16) return true; // Mastercard (2221-2720)
  if (/^3[47]/.test(digits) && len === 15) return true; // Amex
  if (/^6(?:011|5|4[4-9])/.test(digits) && len === 16) return true; // Discover
  return false;
};

const isValidCardLength = (digits) => {
  return digits.length >= 13 && digits.length <= 19;
};

// Luhn algorithm to validate credit card numbers and reduce false positives
const isLuhnValid = (digits) => {
  let sum = 0;
  const chars = digits.split("").reverse();

  chars.forEach((char, index) => {
    let digit = parseInt(char, 10);

    if (index % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
  });

  return sum % 10 === 0;
};

const maskCardNumber = (digits) => {
  const maskedSections = Math.floor(digits.length / 4);
  const remainder = digits.length % 4;
  let masked = Array(maskedSections).fill("xxxx").join("-");

  if (remainder > 0) {
    masked += "-" + "x".repeat(remainder);
  }

  return masked;
};
